import { useCallback, useEffect, useMemo, useState } from "react";
import api, { pickErr } from "@/lib/api";
import { notifyError, notifySuccess } from "@/lib/notify";
import DataTable from "@/components/DataTable";

export default function Questions() {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // filter
  const [filterCategory, setFilterCategory] = useState("");

  // form
  const [text, setText] = useState("");
  const [textHi, setTextHi] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [trait, setTrait] = useState("");
  const [isReverse, setIsReverse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");

  const categoryNameById = useMemo(() => {
    const m = {};
    categories.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [categories]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [qs, cs] = await Promise.all([
        api.get("/questions"),
        api.get("/categories"),
      ]);
      setQuestions(Array.isArray(qs.data) ? qs.data : []);
      setCategories(Array.isArray(cs.data) ? cs.data : []);
    } catch (err) {
      setError(pickErr(err, "Failed to load questions"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () =>
      filterCategory
        ? questions.filter((q) => q.category_id === filterCategory)
        : questions,
    [questions, filterCategory]
  );

  async function onCreate(e) {
    e.preventDefault();
    setFormError("");
    setSuccess("");
    setSubmitting(true);
    try {
      await api.post("/questions", {
        text: text.trim(),
        text_hi: textHi.trim() || null,
        category_id: categoryId,
        trait: trait.trim(),
        is_reverse: isReverse,
      });
      setText("");
      setTextHi("");
      setCategoryId("");
      setTrait("");
      setIsReverse(false);
      setSuccess("Question created.");
      notifySuccess("Question created");
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to create question");
      setFormError(msg);
      notifyError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm("Delete this question?")) return;
    setError("");
    setSuccess("");
    try {
      await api.delete(`/questions/${id}`);
      setSuccess("Question deleted.");
      notifySuccess("Question deleted");
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to delete question");
      setError(msg);
      notifyError(msg);
    }
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Questions</h2>
      <p className="text-sm text-slate-500 mb-2">
        Likert-scale questions. All questions use a fixed 1–5 scale:
      </p>
      <p className="text-xs text-slate-500 mb-6">
        1 = Strongly Disagree · 2 = Disagree · 3 = Neutral · 4 = Agree · 5 =
        Strongly Agree
      </p>

      <section className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">
          Add a question
        </h3>
        <form
          onSubmit={onCreate}
          className="space-y-3"
          data-testid="questions-create-form"
        >
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Question text
            </label>
            <input
              required
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="questions-create-text"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Question text (Hindi) — optional
            </label>
            <input
              value={textHi}
              onChange={(e) => setTextHi(e.target.value)}
              data-testid="questions-create-text-hi"
              placeholder="हिन्दी अनुवाद (वैकल्पिक) — रिक्त छोड़ने पर अंग्रेज़ी दिखाई जाएगी"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Category
              </label>
              <select
                required
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                data-testid="questions-create-category"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Trait
              </label>
              <input
                required
                value={trait}
                onChange={(e) => setTrait(e.target.value)}
                placeholder="e.g. Extraversion"
                data-testid="questions-create-trait"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-6">
              <input
                type="checkbox"
                checked={isReverse}
                onChange={(e) => setIsReverse(e.target.checked)}
                data-testid="questions-create-reverse"
              />
              Reverse-scored
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              data-testid="questions-create-submit"
              className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting ? "Creating…" : "Add question"}
            </button>
            {formError ? (
              <p
                data-testid="questions-create-error"
                className="text-sm text-red-600"
              >
                {formError}
              </p>
            ) : null}
          </div>
        </form>
      </section>

      {success ? (
        <p
          data-testid="questions-success"
          className="mb-4 text-sm text-emerald-700"
        >
          {success}
        </p>
      ) : null}

      <section>
        {(() => {
          const columns = [
            {
              key: "text",
              label: "Question",
              className: "text-slate-900",
            },
            {
              key: "category",
              label: "Category",
              accessor: (r) => categoryNameById[r.category_id] || "",
              render: (r) => categoryNameById[r.category_id] || "—",
              className: "text-slate-700",
            },
            {
              key: "trait",
              label: "Trait",
              className: "text-slate-700",
            },
            {
              key: "is_reverse",
              label: "Reverse",
              accessor: (r) => (r.is_reverse ? "Yes" : "No"),
              className: "text-slate-700",
              align: "center",
            },
          ];
          const actions = (q) => (
            <button
              onClick={() => onDelete(q.id)}
              data-testid={`questions-delete-${q.id}`}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Delete
            </button>
          );
          return (
            <DataTable
              testid="questions"
              columns={columns}
              rows={filtered}
              loading={loading}
              error={error}
              getRowId={(r) => r.id}
              emptyMessage="No questions match."
              defaultSort={{ key: "category", dir: "asc" }}
              exportFilename="questions"
              toolbar={
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  data-testid="questions-filter-category"
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-xs bg-white"
                >
                  <option value="">All categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              }
              actions={actions}
            />
          );
        })()}
      </section>
    </div>
  );
}
